import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowLeft, ExternalLink } from "lucide-react";
import { Reveal } from "../components/common";
import { publicApi } from "../lib/api";
import { ReviewCard } from "../components/GoogleReviewsCarousel";
import Seo from "../components/Seo";

export default function Reviews() {
  const [data, setData] = useState(null);
  useEffect(() => { publicApi.googleRating().then(setData).catch(() => {}); }, []);

  const reviews = data?.reviews || [];

  return (
    <div data-testid="reviews-page">
      <Seo
        title="Google Reviews"
        description="See what our clients say about CASS on Google — genuine reviews from the growing businesses we support with accounting, outsourced finance and Xero across Tamworth, Lichfield, Sutton Coldfield, Birmingham, Solihull and Burton upon Trent."
      />
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <Link to="/testimonials" className="inline-flex items-center gap-2 text-slate-300 hover:text-orange text-sm font-semibold mb-6 transition-colors"><ArrowLeft size={16} /> Back to testimonials</Link>
          <span className="text-orange font-bold text-sm tracking-widest uppercase">Google reviews</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">What our clients say</h1>
          {data?.rating && (
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full pl-3 pr-5 py-2.5 border border-white/10">
                <span className="font-display font-extrabold text-2xl text-orange">{data.rating.toFixed(1)}</span>
                <span className="flex gap-0.5">{[1,2,3,4,5].map((i) => <Star key={i} size={16} className={i <= Math.round(data.rating) ? "fill-orange text-orange" : "fill-white/25 text-white/25"} />)}</span>
                <span className="text-sm text-slate-200 ml-1">{data.total} reviews</span>
              </div>
              {data.url && (
                <a href={data.url} target="_blank" rel="noopener noreferrer" data-testid="see-on-google" className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-orange transition-colors">
                  See all reviews on Google <ExternalLink size={15} />
                </a>
              )}
              {data.write_review_url && (
                <a href={data.write_review_url} target="_blank" rel="noopener noreferrer" data-testid="leave-review-hero" className="inline-flex items-center gap-2 bg-white/10 hover:bg-orange border border-white/20 hover:border-orange text-white text-sm font-bold px-5 py-2.5 rounded-full transition-all">
                  <Star size={15} /> Leave us a review
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          {reviews.length === 0 ? (
            <p className="text-center text-slate-400">Reviews are loading — please check back shortly.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reviews.map((r, i) => (
                  <Reveal key={i} delay={i * 60}><ReviewCard r={r} /></Reveal>
                ))}
              </div>
              {data?.url && (
                <div className="text-center mt-14">
                  <p className="text-slate-500 mb-5">Google shows a selection of our most recent reviews here. To read all {data.total} of them:</p>
                  <div className="flex flex-wrap items-center justify-center gap-4">
                    <a href={data.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">
                      See all {data.total} reviews on Google <ExternalLink size={17} />
                    </a>
                    {data.write_review_url && (
                      <a href={data.write_review_url} target="_blank" rel="noopener noreferrer" data-testid="leave-review-cta" className="inline-flex items-center gap-2 border-2 border-navy hover:bg-navy hover:text-white text-navy font-bold px-8 py-4 rounded-full transition-all">
                        <Star size={17} /> Leave us a review
                      </a>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
